import { useQuery } from "@tanstack/react-query";
import { Check, LoaderCircle, MapPin, Search } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { api } from "../lib/api";
import type { PlaceSuggestion } from "../lib/types";

type Props = {
  value: PlaceSuggestion | null;
  initialText?: string;
  onSelect: (place: PlaceSuggestion | null) => void;
  onInputChange?: () => void;
  autoFocus?: boolean;
  error?: string;
  inputLabel?: string;
  placeholder?: string;
};

export function LocationCombobox({ value, initialText = "", onSelect, onInputChange, autoFocus, error, inputLabel, placeholder = "Search a city, region, or country" }: Props) {
  const listId = useId();
  const [query, setQuery] = useState(value?.display_name || initialText);
  const [debounced, setDebounced] = useState(query);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (value) setQuery(value.display_name);
    else if (initialText) setQuery(initialText);
  }, [value, initialText]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 280);
    return () => window.clearTimeout(timer);
  }, [query]);

  const selectedText = value?.display_name || "";
  const searchEnabled = debounced.length >= 2 && debounced !== selectedText;
  const { data: suggestions = [], isFetching, isError } = useQuery({
    queryKey: ["place-suggestions", debounced],
    queryFn: () => api<PlaceSuggestion[]>(`/places/suggest?q=${encodeURIComponent(debounced)}&limit=7`),
    enabled: searchEnabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const choose = (place: PlaceSuggestion) => {
    setQuery(place.display_name);
    setDebounced(place.display_name);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(place);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!suggestions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % suggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => index <= 0 ? suggestions.length - 1 : index - 1);
    } else if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      choose(suggestions[activeIndex]);
    }
  };

  return <div className="location-field">
    <div className={`location-input${error ? " invalid" : ""}${value ? " verified" : ""}`}>
      <Search size={17} />
      <input
        value={query}
        autoFocus={autoFocus}
        autoComplete="off"
        placeholder={placeholder}
        aria-label={inputLabel}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open && searchEnabled}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={handleKeyDown}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveIndex(-1);
          onInputChange?.();
          onSelect(null);
        }}
      />
      {isFetching ? <LoaderCircle className="location-spinner" size={17} /> : value ? <Check className="location-check" size={17} /> : null}
    </div>
    {open && searchEnabled && <div className="location-results" id={listId} role="listbox">
      {isFetching && !suggestions.length ? <div className="location-message"><LoaderCircle className="location-spinner" size={16} />Finding matching places…</div> : suggestions.map((place, index) => <button
        type="button"
        role="option"
        aria-selected={index === activeIndex}
        id={`${listId}-${index}`}
        className={index === activeIndex ? "active" : ""}
        key={place.provider_id}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => choose(place)}
      ><span className="suggestion-photo">{place.image_url ? <img src={place.image_url} alt={`View of ${place.name}`} loading="lazy" /> : <MapPin size={18} />}</span><span><strong>{place.name}</strong><small>{place.display_name}</small></span>{(place.provider_id.startsWith("google:") || place.country_code) && <i>{place.provider_id.startsWith("google:") ? "Google Places" : place.country_code}</i>}</button>)}
      {!isFetching && !isError && debounced.length >= 2 && !suggestions.length && <div className="location-message">No matching place found. Try adding a state or country.</div>}
      {isError && <div className="location-message error">Place search is unavailable just now. Please try again.</div>}
    </div>}
    {value && <div className="location-confirmation"><Check size={13} /><span><strong>Selected location{value.provider_id.startsWith("google:") ? " · Google Places" : ""}</strong>{value.display_name}{value.timezone ? ` · ${value.timezone.replaceAll("_", " ")}` : ""}</span></div>}
    {error && <span className="field-error">{error}</span>}
  </div>;
}
